import { useCallback, useEffect, useMemo, useState } from 'react'
import { SearchContext } from './search-context-store'

/**
 * Provides global command-palette state and binds the ⌘K / Ctrl+K
 * shortcut (plus "/" when not typing in a field). Keep this high in
 * the tree so any surface — navbar, pages, empty states — can open
 * search via `useSearch()` (exported from `search-context-store`).
 */
export function SearchProvider({ children }: { children: React.ReactNode }) {
  const [isOpen, setIsOpen] = useState(false)

  const open = useCallback(() => setIsOpen(true), [])
  const close = useCallback(() => setIsOpen(false), [])
  const toggle = useCallback(() => setIsOpen((v) => !v), [])

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      const isCmdK =
        (e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k'
      if (isCmdK) {
        e.preventDefault()
        setIsOpen((v) => !v)
        return
      }

      // "/" to open — but not while typing in an input / textarea /
      // contenteditable, where "/" is a literal character.
      if (e.key === '/' && !isOpen) {
        const target = e.target as HTMLElement | null
        const tag = target?.tagName
        const typing =
          tag === 'INPUT' ||
          tag === 'TEXTAREA' ||
          target?.isContentEditable === true
        if (!typing) {
          e.preventDefault()
          setIsOpen(true)
        }
      }
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [isOpen])

  const value = useMemo(
    () => ({ isOpen, open, close, toggle }),
    [isOpen, open, close, toggle],
  )

  return (
    <SearchContext.Provider value={value}>{children}</SearchContext.Provider>
  )
}
